import React, { useCallback } from "react";
import { map } from "ramda";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import { authTypes } from "./authenticationService";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import MenuList from "@mui/material/MenuList";
import MenuItem from "@mui/material/MenuItem";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Grow from "@mui/material/Grow";
import { Box } from "@mui/material";
import PropTypes from "prop-types";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";

const AuthenticationListButton = ({
  title,
  onAuthenticationSelected,
  variant = "icon",
}) => {
  const anchorRef = React.useRef(null);
  const [authenticationMenuOpen, setAuthenticationMenuOpen] = React.useState(
    false,
  );

  // return focus to the button when we transitioned from !open -> open
  const prevOpen = React.useRef(authenticationMenuOpen);
  React.useEffect(() => {
    if (prevOpen.current === true && authenticationMenuOpen === false) {
      anchorRef.current.focus();
    }

    prevOpen.current = authenticationMenuOpen;
  }, [authenticationMenuOpen]);

  const handleListKeyDown = useCallback(event => {
    if (event.key === "Tab") {
      event.preventDefault();
      setAuthenticationMenuOpen(false);
    }
  }, []);

  const handleNewAuthenticationClicked = useCallback(() => {
    setAuthenticationMenuOpen(prevOpen => !prevOpen);
  }, []);

  const handleAuthenticationMenuClose = useCallback(event => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }

    setAuthenticationMenuOpen(false);
  }, []);

  const handleAuthenticationMenuSelect = useCallback(
    value => {
      onAuthenticationSelected(value);
      setAuthenticationMenuOpen(false);
    },
    [onAuthenticationSelected],
  );

  return (
    <Box>
      {variant === "button" ? (
        <Button
          ref={anchorRef}
          variant={"text"}
          startIcon={<AddIcon />}
          aria-controls={authenticationMenuOpen ? "menu-list-grow" : undefined}
          aria-haspopup="true"
          onClick={handleNewAuthenticationClicked}
        >
          {title}
        </Button>
      ) : (
        <Tooltip title={title}>
          <IconButton
            ref={anchorRef}
            aria-controls={
              authenticationMenuOpen ? "menu-list-grow" : undefined
            }
            aria-haspopup="true"
            onClick={handleNewAuthenticationClicked}
            size="large"
          >
            <AddIcon color="primary" fontSize="large" />
          </IconButton>
        </Tooltip>
      )}
      <Popper
        open={authenticationMenuOpen}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        placement="bottom"
        style={{ zIndex: 100 }}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === "bottom" ? "center top" : "center bottom",
            }}
          >
            <Paper variant={"outlined"}>
              <ClickAwayListener onClickAway={handleAuthenticationMenuClose}>
                <MenuList
                  autoFocusItem={authenticationMenuOpen}
                  id="menu-list-grow"
                  onKeyDown={handleListKeyDown}
                >
                  {map(
                    at => (
                      <MenuItem
                        key={at.value}
                        onClick={() => handleAuthenticationMenuSelect(at.value)}
                      >
                        {at.display}
                      </MenuItem>
                    ),
                    authTypes,
                  )}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </Box>
  );
};

AuthenticationListButton.propTypes = {
  title: PropTypes.string,
  onAuthenticationSelected: PropTypes.func,
  variant: PropTypes.oneOf(["button", "icon"]),
};

export default AuthenticationListButton;
